import { getJwtToken } from "./token";
import { getClientSessionId } from "./session";

/**
 * Browser-side error shipper — the web counterpart to the mobile app's
 * logShipper. Uncaught exceptions, unhandled promise rejections and React
 * render crashes are batched here and POSTed to /client-logs/web, where they
 * land in web-%DATE%.log and surface under System → Web Error Logs.
 *
 * Before this existed, a tester who hit a blank screen had nothing to hand
 * back but a description. Now the stack trace, the route and the browser are
 * already waiting in the admin viewer.
 *
 * Design constraints that are easy to get wrong:
 *   - A failing ship must never surface UI, and must never log the user out.
 *     That is why this module calls `fetch` directly instead of going through
 *     utils/request.ts (which toasts on network error and can trigger a token
 *     refresh / forceLogout on a 401). This is a deliberate, narrow exception
 *     to the "always use request.ts" rule.
 *   - A render loop must not be able to flood the endpoint or eat memory, so
 *     the queue is capped and identical entries are de-duplicated.
 *   - Nothing in this file may itself call logClientError — that would recurse.
 */

export type ClientLogLevel = "error" | "warn" | "info";

interface PendingEntry {
    level: ClientLogLevel;
    message: string;
    detail?: string;
    url: string;
    ts: number;
}

/** Ship as soon as this many entries are queued. Kept small so a batch stays under the 64KB `keepalive` cap. */
const BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 10000;
/** Beyond this the oldest entries are dropped — a render loop must not grow the heap. */
const MAX_QUEUE = 100;
/** An identical message+detail within this window ships once. */
const DEDUPE_WINDOW_MS = 60000;
const MESSAGE_MAX = 1000;
const DETAIL_MAX = 2000;

const APP_VERSION = process.env.REACT_APP_VERSION || "dev";

let queue: PendingEntry[] = [];
const recentKeys = new Map<string, number>();
let flushTimer: ReturnType<typeof setInterval> | null = null;
let isFlushing = false;
let isInstalled = false;

/**
 * A short, *retained* history of what went wrong recently — separate from
 * `queue`, which is emptied on every flush. The Report-a-bug capture attaches
 * this, so a report filed a minute after the error still carries it.
 */
export interface RecentClientError {
    level: ClientLogLevel;
    message: string;
    at: string;
}

const MAX_RECENT_ERRORS = 10;
const recentErrors: RecentClientError[] = [];

/**
 * The last (up to 10) client errors this tab saw, oldest first.
 * @returns A copy of the retained error buffer.
 */
export const getRecentClientErrors = (): RecentClientError[] => [...recentErrors];

const newMutationId = (): string => {
    const cryptoRef = typeof crypto !== "undefined" ? (crypto as any) : undefined;
    if (cryptoRef?.randomUUID) return cryptoRef.randomUUID();
    return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/**
 * Reduce anything thrown (Error, string, rejected non-Error value) to a
 * message + stack pair. Rejections carrying a plain object are JSON-stringified
 * so a `{ status, body }` rejection still tells us something useful.
 */
const describe = (error: unknown): { message: string; detail?: string } => {
    if (error instanceof Error) {
        return { message: error.message || error.name, detail: error.stack };
    }
    if (typeof error === "string") {
        return { message: error };
    }
    try {
        return { message: JSON.stringify(error) };
    } catch {
        return { message: String(error) };
    }
};

/** True when this exact entry was already queued inside the dedupe window. */
const isDuplicate = (key: string, now: number): boolean => {
    const last = recentKeys.get(key);
    if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return true;
    recentKeys.set(key, now);
    // Opportunistic prune so the dedupe map can't grow without bound.
    if (recentKeys.size > MAX_QUEUE * 2) {
        recentKeys.forEach((at, k) => {
            if (now - at >= DEDUPE_WINDOW_MS) recentKeys.delete(k);
        });
    }
    return false;
};

const ship = async (batch: PendingEntry[]): Promise<void> => {
    const token = getJwtToken();
    // The endpoint is @Authorized(). With no token there is nobody to attribute
    // the error to and the POST would 401, so drop it rather than retry.
    if (!token) return;

    const sessionId = getClientSessionId();

    await fetch(process.env.REACT_APP_API_ENDPOINT + "client-logs/web", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: token,
            "X-Session-Id": sessionId,
        },
        credentials: "include",
        // Lets an in-flight batch survive the page unload that often follows a crash.
        keepalive: true,
        body: JSON.stringify({
            entries: batch.map(e => ({
                level: e.level,
                message: e.message,
                detail: e.detail,
                url: e.url,
                ts: e.ts,
            })),
            appVersion: APP_VERSION,
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
            // Stamped onto every resulting web-log line as [sess:...], which is
            // how a bug report from this tab links to these errors.
            sessionId,
            clientMutationId: newMutationId(),
        }),
    });
};

/**
 * Send whatever is queued. Best-effort: a failed batch is dropped rather than
 * re-queued, because retrying on a broken connection is how you build an
 * infinite storm — and the entry is still in the user's console either way.
 */
export const flushClientLogs = async (): Promise<void> => {
    if (isFlushing || queue.length === 0) return;
    if (!getJwtToken()) {
        queue = [];
        return;
    }

    isFlushing = true;
    const batch = queue.slice(0, BATCH_SIZE);
    queue = queue.slice(batch.length);
    try {
        await ship(batch);
    } catch {
        // Swallowed on purpose. Logging this failure would recurse straight
        // back into the shipper, and toasting it would blame the user for a
        // background task they never asked for.
    } finally {
        isFlushing = false;
    }
};

/**
 * Queue a client-side error for shipping.
 * @param error - The thrown value (Error, string, or anything else).
 * @param context - Optional extras: `source` (where it was caught), `componentStack` (React), `label` (route/widget name).
 * @param level - Severity; defaults to "error".
 */
export const logClientError = (
    error: unknown,
    context?: { source?: string; componentStack?: string; label?: string },
    level: ClientLogLevel = "error",
): void => {
    try {
        const { message, detail } = describe(error);
        const labelled = context?.label ? `[${context.label}] ${message}` : message;

        const detailParts: string[] = [];
        if (detail) detailParts.push(detail);
        if (context?.componentStack) detailParts.push("Component stack:" + context.componentStack);
        if (context?.source) detailParts.push("Source: " + context.source);

        const now = Date.now();
        const entry: PendingEntry = {
            level,
            message: labelled.slice(0, MESSAGE_MAX),
            detail: detailParts.length > 0 ? detailParts.join("\n\n").slice(0, DETAIL_MAX) : undefined,
            url: typeof window !== "undefined" ? window.location.pathname + window.location.search : "?",
            ts: now,
        };

        if (isDuplicate(`${entry.level}|${entry.message}|${entry.detail ?? ""}`, now)) return;

        recentErrors.push({ level: entry.level, message: entry.message, at: new Date(now).toISOString() });
        if (recentErrors.length > MAX_RECENT_ERRORS) recentErrors.shift();

        queue.push(entry);
        if (queue.length > MAX_QUEUE) queue = queue.slice(queue.length - MAX_QUEUE);
        if (queue.length >= BATCH_SIZE) void flushClientLogs();
    } catch {
        // The shipper must never be the thing that breaks the page.
    }
};

/**
 * Install the global handlers. Called once from index.tsx. Safe to call twice.
 */
export const installClientErrorLogging = (): void => {
    if (isInstalled || typeof window === "undefined") return;
    isInstalled = true;

    window.addEventListener("error", (event: ErrorEvent) => {
        // Failed resource loads (<img>, <script>) also fire "error" but carry no
        // `error` object — those are noise, not application faults.
        if (!event.error && !event.message) return;
        logClientError(event.error ?? event.message, {
            source: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : "window.onerror",
        });
    });

    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
        logClientError(event.reason, { source: "unhandledrejection" });
    });

    flushTimer = setInterval(() => { void flushClientLogs(); }, FLUSH_INTERVAL_MS);

    // Last chance to ship before the tab goes away. `pagehide` fires in cases
    // `beforeunload` does not (bfcache, mobile tab switch).
    window.addEventListener("pagehide", () => { void flushClientLogs(); });
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") void flushClientLogs();
    });
};

/** Tear down the interval. Only used by tests; the app never uninstalls. */
export const uninstallClientErrorLogging = (): void => {
    if (flushTimer) {
        clearInterval(flushTimer);
        flushTimer = null;
    }
    isInstalled = false;
};
